import { inject } from '@angular/core';
import { type ResolveFn, Routes } from '@angular/router';

import { TranslocoService, provideTranslocoScope } from '@jsverse/transloco';

import {
  AUTHENTICATION_ROUTES,
  AuthGuard,
  CanMatchFullUsername,
  FullUsernameGuard,
  LOGOUT_ROUTE,
  PROXY_AUTHENTICATION_ROUTES,
  THIRD_PARTY_SSO_ROUTES,
  ValidCompanyStatusForCustomerGuard,
  WELCOME_ROUTES,
} from '@finverity/authentication';
import { HasAccessGuardFactory } from '@finverity/identity-access';
import { CurrenciesService } from '@finverity/platform-app/shared/services';

import {
  CanMatchNotificationTemplatesRoute,
  CanMatchNplApprovalsRoute,
  CanMatchUnauthenticatedLayout,
} from './core/guards';
import { getNavigationSidebarProviders } from './core/layout/components/navigation-sidebar';
import { NAVIGATION_MENU_IDENTITY_ACCESS_PROVIDER } from './core/layout/identity-access';
import { provideHeaderNavigationMenuItems } from './core/layout/providers';
import {
  REQUIRED_CONTRACTS_IDENTITY_ACCESS_PROVIDER,
  RequiredContractsPermission,
} from './modules/company-onboarding/customer/sections/required-contracts-for-signature/identity-access';
import { CONFIGURE_APPROVAL_WORKFLOW_SETTINGS_ROUTE_PARAM_NAME } from './modules/configure-approval-workflow-settings/constants';
import INSTRUMENT_APPROVAL_ROUTES from './modules/instrument-approval/shell/instrument-approval.routes';
import { SELF_ONBOARDING_ROUTES } from './modules/self-onboarding/shell';

const browserTitleResolver =
  (key: string): ResolveFn<string> =>
  () =>
    inject(TranslocoService).translate(key);

/**
 * Loads the currency configuration before the authenticated shell activates.
 *
 * Around thirty authenticated screens read `currencyListForSelect`, `defaultCurrency` and
 * `currencySettings` synchronously, so the data has to be in place before any of them is
 * constructed. Resolving it on the authenticated route gives that guarantee without putting the
 * request on the unauthenticated critical path: no signed-out screen shows a currency, and the
 * endpoint expects a token the visitor does not have yet.
 *
 * Failures are swallowed so that an unavailable currency service degrades the affected fields
 * rather than cancelling the navigation and leaving the user on a blank page.
 *
 * On a reload the request is already in flight: the bootstrap warm-up in `app.config.ts` starts it
 * alongside the auth check whenever a token is present, and this resolver joins that request
 * instead of waiting for the guards to finish and then opening a second one.
 */
const currencyConfigurationResolver: ResolveFn<unknown> = () =>
  inject(CurrenciesService).loadCurrencyConfigurationOnce();

export const APP_ROUTES: Routes = [
  ...THIRD_PARTY_SSO_ROUTES,

  // Unauthenticated routes
  {
    path: '',
    canMatch: [CanMatchUnauthenticatedLayout],
    loadComponent: () => import('./core/layout/unauthenticated-layout/unauthenticated-layout.component'),
    children: AUTHENTICATION_ROUTES,
  },
  {
    path: '',
    canMatch: [CanMatchFullUsername],
    loadComponent: () => import('./core/layout/unauthenticated-layout/unauthenticated-layout.component'),
    children: WELCOME_ROUTES,
  },
  {
    path: 'landing-page/:landingPageId/:interaction',
    loadComponent: () =>
      import('./modules/self-onboarding/features/landing-pages/features/public-landing-page/public-landing-page.component'),
  },

  // Logout routes
  {
    ...LOGOUT_ROUTE,
    canActivate: [AuthGuard],
  },

  // Authenticated routes
  {
    path: '',
    canActivate: [AuthGuard, FullUsernameGuard, ValidCompanyStatusForCustomerGuard],
    resolve: { currencyConfiguration: currencyConfigurationResolver },
    loadComponent: () => import('./core/layout/authenticated-layout/authenticated-layout.component'),
    providers: [
      NAVIGATION_MENU_IDENTITY_ACCESS_PROVIDER,
      getNavigationSidebarProviders(),
      provideHeaderNavigationMenuItems(),
    ],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'oauth/access-result',
        loadComponent: () => import('@finverity/authentication').then(m => m.SsoLoginAccessResultContainerComponent),
      },

      {
        path: 'dashboard',
        loadChildren: () => import('./modules/dashboard/shell/dashboard.routes'),
      },
      {
        path: 'companies',
        loadChildren: () => import('./modules/companies/shell/companies.routes'),
      },
      ...SELF_ONBOARDING_ROUTES,
      {
        path: 'company-onboarding',
        loadChildren: () => import('./modules/company-onboarding/customer/shell/company-onboarding.routes'),
      },
      {
        path: 'company-onboarding',
        loadChildren: () => import('./modules/company-onboarding/funder/shell/company-onboarding.routes'),
      },
      {
        path: 'company-details',
        loadChildren: () => import('./modules/company-details/shell/company-details.routes'),
      },
      {
        path: 'product-structure-management',
        loadChildren: () => import('./modules/product-structure-management/shell/product-structure-management.routes'),
      },
      {
        path: 'product-group-management',
        loadChildren: () => import('./modules/product-group-management/shell/product-group-management.routes'),
      },
      {
        path: 'fx-rate-management',
        loadChildren: () => import('./modules/fx-rate-management/shell/fx-rate-management.routes'),
      },
      {
        path: 'floating-rate-management',
        loadChildren: () => import('./modules/floating-rate-management/shell/floating-rate-management.routes'),
      },
      {
        path: 'programmes',
        loadChildren: () => import('./modules/programmes/shell/programmes.routes'),
      },
      {
        path: 'deals',
        loadChildren: () => import('./modules/deals-management/shell/deals-management.routes'),
      },
      {
        path: 'deal',
        loadChildren: () => import('./modules/deal/shell/deal-wizard.routes'),
      },
      {
        path: 'bank-holiday-rules',
        loadChildren: () => import('./modules/bank-holiday-rules/shell/bank-holiday-rules.routes'),
      },
      {
        path: 'contract-templates',
        loadChildren: () => import('./modules/contract-templates/shell/contract-templates.routes'),
      },
      {
        path: 'contracts/:contractId/view',
        title: browserTitleResolver('contract-templates.routeTitles.viewContract'),
        providers: [
          REQUIRED_CONTRACTS_IDENTITY_ACCESS_PROVIDER,
          provideTranslocoScope({ scope: 'contract-templates' }),
        ],
        canActivate: [HasAccessGuardFactory(RequiredContractsPermission.ViewGeneratedContracts)],
        loadComponent: () =>
          import('./modules/contract-templates/features/contract-view-page').then(
            module => module.ContractViewPageComponent
          ),
      },
      {
        path: 'webhook-management',
        loadChildren: () => import('./modules/webhook-management/shell/webhook-management.routes'),
      },
      {
        path: 'system-health-notifications',
        loadChildren: () => import('./modules/system-health-notifications/shell/system-health-notifications.routes'),
      },
      {
        path: 'credit-note-rules',
        loadChildren: () => import('./modules/credit-note-rules/shell/credit-note-rules.routes'),
      },
      {
        path: 'custom-fields-management',
        loadChildren: () => import('./modules/custom-fields-management/shell/custom-fields-management.routes'),
      },
      {
        path: 'instrument-upload',
        loadChildren: () => import('./modules/instrument-upload/shell/instrument-upload.routes'),
      },
      {
        path: 'allocated-credit-notes',
        loadChildren: () => import('./modules/allocated-credit-notes/shell/allocated-credit-notes.routes'),
      },
      {
        path: 'npl-approvals',
        canMatch: [CanMatchNplApprovalsRoute],
        loadChildren: () => import('./modules/npl-approvals/shell/npl-approvals.routes'),
      },
      {
        path: 'disbursement-files',
        loadChildren: () => import('./modules/disbursements/shell/disbursements.routes'),
      },
      {
        path: 'incoming-payments',
        loadChildren: () => import('./modules/payments/shell/payments.routes'),
      },
      ...INSTRUMENT_APPROVAL_ROUTES,
      {
        path: 'instrument-approval',
        loadChildren: () => import('./modules/instrument-approval/shell/instrument-approval.routes'),
      },
      {
        path: 'ready-for-funding',
        loadChildren: () => import('./modules/instrument-funding/shell/instrument-funding.routes'),
      },
      {
        path: 'disbursement-confirmation',
        loadChildren: () => import('./modules/instrument-disbursements/shell/instrument-disbursements.routes'),
      },
      {
        path: 'repayments',
        loadChildren: () => import('./modules/repayments/shell/repayments.routes'),
      },
      {
        path: 'user-management',
        loadChildren: () => import('./modules/user-management/shell/user-management.routes'),
      },
      {
        path: 'service-account-management',
        loadChildren: () => import('./modules/service-account-management/shell/service-account-management.routes'),
      },
      {
        path: 'company-security-settings',
        loadChildren: () => import('./modules/company-security-settings/shell/company-security-settings.routes'),
      },
      {
        path: 'audit-trail',
        loadChildren: () => import('./modules/audit-trail/shell/audit-trail.routes'),
      },
      {
        path: 'company-notifications',
        loadChildren: () => import('./modules/company-notification/shell/company-notification.routes'),
      },
      {
        path: 'user-profile',
        loadChildren: () => import('./modules/user-profile/shell/user-profile.routes'),
      },
      {
        path: 'notifications',
        loadChildren: () => import('./modules/notifications/shell/notifications.routes'),
      },
      {
        path: 'notification-settings',
        loadChildren: () => import('./modules/notification-settings/shell/notification-settings.routes'),
      },
      {
        path: `configure-approval-workflow-settings/:${CONFIGURE_APPROVAL_WORKFLOW_SETTINGS_ROUTE_PARAM_NAME}`,
        loadChildren: () =>
          import('./modules/configure-approval-workflow-settings/shell/configure-approval-workflow-settings.routes'),
      },
      {
        path: 'outstanding-instruments',
        loadChildren: () =>
          import('./modules/outstanding-instrument-repayment/shell/outstanding-instrument-repayment.routes'),
      },
      {
        path: 'customers-upload',
        loadChildren: () => import('./modules/customers-upload/shell/customers-upload.routes'),
      },
      {
        path: 'sso',
        loadChildren: () => import('./modules/sso-management/shell/sso-management.routes'),
      },
      ...PROXY_AUTHENTICATION_ROUTES,
      {
        path: 'master-registration-form',
        loadChildren: () =>
          import('./modules/onboarding-master-registration-form/shell/onboarding-master-registration-form.routes'),
      },
      {
        path: 'bulk-supporting-documents-upload',
        loadChildren: () =>
          import('./modules/bulk-supporting-documents-upload/shell/bulk-supporting-documents-upload.routes'),
      },
      {
        path: 'notification-templates',
        canMatch: [CanMatchNotificationTemplatesRoute],
        loadChildren: () => import('./modules/notification-templates/shell/notification-templates.routes'),
      },
    ],
  },

  { path: '**', pathMatch: 'full', redirectTo: '/dashboard' },
];

// export const UI_KIT_ROUTES: Routes = [
//   {
//     path: 'ui-kit',
//     loadChildren: () => import('@finverity/ui-kit/documentation/shell/documentation.routes'),
//   },
// ];
